/**
 * LanguageSelector — selector de idioma del tier.
 *
 * REQ-PWB-001 (web pública CP): variante `dropdown` (tema claro/oscuro) además de la
 * segmentada histórica. Compatibilidad garantizada: `variant="segmented"` y
 * `theme="light"` son los valores por defecto — el render de los consumidores actuales
 * (agent-ui, admin-console, account-ui) no cambia.
 *
 * La variante dropdown es accesible: `aria-haspopup="listbox"`/`aria-expanded`,
 * opciones `role="option"` con `aria-selected`, cierre con `Escape` y click-fuera,
 * foco visible y objetivos táctiles ≥44 px.
 */

import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../lib/utils';
import { useI18n } from './I18nProvider';

const languages = [
  { code: 'en' as const, label: 'English', flag: '🇺🇸' },
  { code: 'es' as const, label: 'Español', flag: '🇪🇸' },
];

export interface LanguageSelectorProps {
  /** `segmented` (default, comportamiento histórico) · `dropdown` (web pública). */
  variant?: 'segmented' | 'dropdown';
  /** Tema visual de la variante dropdown. */
  theme?: 'light' | 'dark';
}

function LanguageDropdown({ theme }: { theme: 'light' | 'dark' }) {
  const { locale, setLocale } = useI18n();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const active = languages.find((lang) => lang.code === locale) ?? languages[0];
  const isDark = theme === 'dark';

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative" data-testid="language-selector">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${active.label} — ${active.code.toUpperCase()}`}
        title={active.label}
        data-testid="language-selector-trigger"
        className={cn(
          'inline-flex min-h-[44px] items-center gap-2 rounded-lg border px-3 text-sm transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
          isDark
            ? 'border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700'
            : 'border-gray-200 bg-gray-100 text-gray-900 hover:bg-gray-200',
        )}
      >
        <span aria-hidden="true">{active.flag}</span>
        <span className="font-medium">{active.code.toUpperCase()}</span>
        <ChevronDown
          className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={active.label}
          data-testid="language-selector-menu"
          className={cn(
            'absolute right-0 z-50 mt-2 min-w-[10rem] overflow-hidden rounded-lg border py-1 shadow-lg',
            isDark
              ? 'border-slate-600 bg-slate-800 text-slate-100'
              : 'border-gray-200 bg-white text-gray-900',
          )}
        >
          {languages.map((lang) => {
            const isActive = locale === lang.code;
            return (
              <button
                key={lang.code}
                type="button"
                role="option"
                aria-selected={isActive}
                data-testid={`language-option-${lang.code}`}
                onClick={() => {
                  setLocale(lang.code);
                  setOpen(false);
                }}
                className={cn(
                  'flex min-h-[44px] w-full items-center gap-2 px-3 text-left text-sm transition-colors',
                  isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-50',
                  isActive && (isDark ? 'bg-slate-700/70 font-medium' : 'bg-gray-100 font-medium'),
                )}
              >
                <span aria-hidden="true">{lang.flag}</span>
                <span className="flex-1">{lang.label}</span>
                {isActive && <Check className="h-4 w-4" aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function LanguageSelector({
  variant = 'segmented',
  theme = 'light',
}: LanguageSelectorProps = {}) {
  const { locale, setLocale } = useI18n();

  if (variant === 'dropdown') {
    return <LanguageDropdown theme={theme} />;
  }

  return (
    <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1" data-testid="language-selector">
      {languages.map((lang) => (
        <button
          key={lang.code}
          onClick={() => setLocale(lang.code)}
          className={[
            'px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-2',
            locale === lang.code
              ? 'bg-white text-gray-900 shadow-sm font-medium'
              : 'text-gray-500 hover:text-gray-700',
          ].join(' ')}
          title={lang.label}
        >
          <span>{lang.flag}</span>
          <span className="hidden sm:inline">{lang.code.toUpperCase()}</span>
        </button>
      ))}
    </div>
  );
}
